import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import { SpecChecklist } from "../components/sell/SpecChecklist";
import { FEATURES } from "../lib/features";
import { SAUDI_CITIES as CITIES } from "../data/locations";
import { validateDescription, DESCRIPTION_MAX_LEN } from "../lib/listingDescription";
import SaleOptionsChips from "../components/SaleOptionsChips";
import {
  ArrowRight, Loader2, Upload as UploadIcon, X, GripVertical, Phone, MessageSquare,
  ShieldCheck, Trash2, AlertTriangle,
} from "lucide-react";

const VEHICLE_TYPES = [
  ["used", "Used", "مستعملة"],
  ["new", "New (dealer)", "جديدة (تاجر)"],
  // iter276 — mirrors SellPage: makes the state selectable when
  // editing an existing listing that qualifies. Setting it is opt-in
  // per listing; nothing gets auto-tagged.
  ["new_customs_card", "New — Customs Card", "🆕 جديدة – بطاقة جمركية"],
  ["rental", "Rental", "تأجير"],
  ["salvage", "Salvage", "سكراب"],
  ["motorcycle", "Motorcycle", "دراجة"],
  ["heavy", "Heavy Equipment", "المعدات الثقيلة"],
  ["export", "Export-ready", "للتصدير"],
];

const BODY_TYPES = [
  ["Sedan", "Sedan", "سيدان"],
  ["SUV", "SUV", "دفع رباعي (SUV)"],
  ["Crossover", "Crossover", "كروس أوفر"],
  ["Off-road", "Off-road", "دفع رباعي (طرق وعرة)"],
  ["Hatchback", "Hatchback", "هاتشباك"],
  ["Coupe", "Coupe", "كوبيه"],
  ["Convertible", "Convertible", "مكشوفة"],
  ["Pickup", "Pickup", "بيك أب"],
  ["Van", "Van", "فان"],
  ["Truck", "Truck", "شاحنة"],
  ["Other", "Other", "أخرى"],
];

// iter188 — unified city registry. See /app/frontend/src/data/locations.js.

const TRANSMISSIONS = [
  ["Automatic", "Automatic", "أوتوماتيك"],
  ["Manual", "Manual", "عادي"],
  ["DCT", "DCT", "DCT"],
  ["CVT", "CVT", "CVT"],
];

const FUELS = [
  ["Petrol", "Petrol", "بنزين"],
  ["Diesel", "Diesel", "ديزل"],
  ["Hybrid", "Hybrid", "هايبرد"],
  ["Electric", "Electric", "كهربائي"],
];

const MAX_IMAGES = 8;

export default function EditListingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/vehicles/${id}`)
      .then(({ data }) => {
        setForm({
          type: data.type || "used",
          make: data.make || "",
          model: data.model || "",
          year: data.year || 2023,
          price_sar: data.price_sar || 0,
          mileage_km: data.mileage_km || 0,
          transmission: data.transmission || "Automatic",
          fuel: data.fuel || "Petrol",
          color: data.color || "",
          city: data.city || "Riyadh",
          category: data.category || "cars",
          body: data.body || "Sedan",
          description: data.description || "",
          specs: data.specs || {},
          images: Array.isArray(data.images) && data.images.length > 0
            ? data.images
            : (data.image ? [data.image] : []),
          contact_phone: data.contact_phone || "",
          contact_whatsapp: data.contact_whatsapp || "",
          prefer_contact: data.prefer_contact || "phone",
          periodic_inspection_valid: !!data.periodic_inspection_valid,
          periodic_inspection_expiry: data.periodic_inspection_expiry || "",
          escrow_enabled: !!data.escrow_enabled,
          sale_options: Array.isArray(data.sale_options) ? data.sale_options : [],
          price_visibility: data.price_visibility || "shown",
          _seller_id: data.seller_id || data.owner_id,
        });
      })
      .catch((e) => setLoadErr(e.response?.data?.detail || e.message));
  }, [id]);

  if (user === undefined) return <div className="p-10 text-[#6b6b6b]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: `/vehicle/${id}/edit` }} replace />;
  if (loadErr) return <div className="p-10 text-red-600">{String(loadErr)}</div>;
  if (!form) return <div className="p-10 text-[#6b6b6b]">{t("common.loading")}</div>;

  const isOwner = form._seller_id === user.id;
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  if (!isOwner && !isAdmin) {
    return (
      <div className="max-w-[800px] mx-auto p-10 text-center">
        <AlertTriangle className="w-10 h-10 text-[#8a6f2e] mx-auto mb-3" />
        <h2 className="font-display text-2xl mb-2">
          {lang === "ar" ? "ليست لديك صلاحية تعديل هذا الإعلان" : "You don't have permission to edit this listing"}
        </h2>
        <Link to={`/vehicle/${id}`} className="btn-primary mt-4 inline-flex">
          {lang === "ar" ? "العودة للإعلان" : "Back to listing"}
        </Link>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (form.images.length + files.length > MAX_IMAGES) {
      setErr(lang === "ar" ? `الحد الأقصى ${MAX_IMAGES} صور.` : `Max ${MAX_IMAGES} photos.`);
      return;
    }
    setUploadBusy(true);
    setErr(null);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await apiClient.post("/upload?kind=vehicle", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploadBusy(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  // Drag-and-drop reorder (mouse via HTML5 DnD, touch via the grip handle).
  const moveImage = (from, to) => {
    if (from === null || to === null || from === to) return;
    setForm((f) => {
      const imgs = [...f.images];
      const [m] = imgs.splice(from, 1);
      imgs.splice(to, 0, m);
      return { ...f, images: imgs };
    });
  };
  const touchOverIdx = (e) => {
    const t = e.touches[0] || e.changedTouches[0];
    const el = document.elementFromPoint(t.clientX, t.clientY)?.closest("[data-img-idx]");
    return el ? Number(el.getAttribute("data-img-idx")) : null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (form.images.length === 0) {
        throw new Error(lang === "ar" ? "أضف صورة واحدة على الأقل." : "Please upload at least one photo.");
      }
      if (!form.contact_phone && !form.contact_whatsapp && form.prefer_contact !== "chat") {
        throw new Error(lang === "ar" ? "أضف رقم جوال أو واتساب." : "Provide phone or WhatsApp.");
      }
      // iter220 — enforce description policy before submit.
      const _descCheck = validateDescription(form.description);
      if (!_descCheck.ok) {
        throw new Error(lang === "ar" ? _descCheck.message_ar : _descCheck.message_en);
      }
      const payload = {
        type: form.type,
        make: form.make,
        model: form.model,
        year: Number(form.year),
        price_sar: Number(form.price_sar),
        mileage_km: Number(form.mileage_km),
        transmission: form.transmission,
        fuel: form.fuel,
        color: form.color,
        city: form.city,
        category: form.category,
        body: form.body,
        description: form.description,
        specs: form.specs || {},
        images: form.images,
        image: form.images[0],
        contact_phone: form.contact_phone,
        contact_whatsapp: form.contact_whatsapp,
        prefer_contact: form.prefer_contact,
        periodic_inspection_valid: form.periodic_inspection_valid,
        periodic_inspection_expiry: form.periodic_inspection_expiry,
        escrow_enabled: form.escrow_enabled,
        price_visibility: form.price_visibility || "shown",
        sale_options: Array.isArray(form.sale_options) ? form.sale_options : [],
      };
      await apiClient.put(`/vehicles/${id}`, payload);
      nav(`/vehicle/${id}`, { state: { justUpdated: true } });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDelBusy(true);
    try {
      await apiClient.delete(`/vehicles/${id}`);
      nav("/dashboard", { state: { deleted: true } });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setDelBusy(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="edit-listing-page">
      <Link to={`/vehicle/${id}`} className="text-sm text-[#6b6b6b] hover:text-[#0a0a0a]">
        ← {lang === "ar" ? "العودة للإعلان" : "Back to listing"}
      </Link>
      <div className="overline mt-3 mb-2">{lang === "ar" ? "تعديل الإعلان" : "EDIT LISTING"}</div>
      <h1 className="font-display text-4xl tracking-tight font-medium">
        {lang === "ar" ? "تعديل إعلان السيارة" : "Edit your vehicle listing"}
      </h1>
      <p className="text-[#6b6b6b] mt-2 max-w-2xl">
        {lang === "ar"
          ? "حدّث معلومات السيارة أو احذف الإعلان نهائياً."
          : "Update your vehicle's information or remove the listing permanently."}
      </p>

      <form onSubmit={submit} className="surface rounded-3xl p-8 mt-8 space-y-5" data-testid="edit-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع الإعلان" : "Listing type"}</label>
            <select className="input-dark mt-1" value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="edit-type">
              {VEHICLE_TYPES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "المدينة" : "City"}</label>
            <select className="input-dark mt-1" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="edit-city">
              {CITIES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{t("common.make")}</label>
            <input className="input-dark mt-1" required value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="edit-make" />
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{t("common.model")}</label>
            <input className="input-dark mt-1" required value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="edit-model" />
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{t("common.year")}</label>
            <input type="number" className="input-dark mt-1" required value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="edit-year" />
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "العدّاد (كم)" : "Mileage (km)"}</label>
            <input type="number" className="input-dark mt-1" value={form.mileage_km} onChange={(e) => set("mileage_km", e.target.value)} data-testid="edit-mileage" />
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "السعر (ريال)" : "Price (SAR)"}</label>
            <input type="number" className="input-dark mt-1" required value={form.price_sar} onChange={(e) => set("price_sar", e.target.value)} data-testid="edit-price" />
          </div>
          <div className="sm:col-span-2">
            <div className="rounded-xl border border-[#0a0a0a]/[0.08] bg-[#fafaf7] p-3" data-testid="edit-price-visibility">
              <div className="text-[12px] font-bold text-[#0a0a0a] mb-2">
                {lang === "ar" ? "طريقة عرض السعر" : "Price display"}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "shown", ar: "إظهار السعر", en: "Show price", desc_ar: "يظهر السعر مباشرة", desc_en: "Visible to all" },
                  { id: "hidden", ar: "إخفاء السعر واستقبال العروض", en: "Hide price · get offers", desc_ar: "المهتم يقدّم عرضًا خاصًا", desc_en: "Buyers submit private offers" },
                ].map((opt) => {
                  const active = (form.price_visibility || "shown") === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => set("price_visibility", opt.id)}
                      className={`text-start rounded-xl p-3 border transition-all ${
                        active
                          ? "border-[#c9a55c] bg-white shadow-sm"
                          : "border-[#0a0a0a]/10 bg-white hover:border-[#c9a55c]/40"
                      }`}
                      data-testid={`edit-price-visibility-${opt.id}`}
                    >
                      <div className="text-[12px] font-semibold text-[#0a0a0a]">
                        {lang === "ar" ? opt.ar : opt.en}
                      </div>
                      <div className="text-[10px] text-[#8a8a8a] mt-0.5">
                        {lang === "ar" ? opt.desc_ar : opt.desc_en}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع الهيكل" : "Body type"}</label>
            <select className="input-dark mt-1" value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="edit-body">
              {BODY_TYPES.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "ناقل الحركة" : "Transmission"}</label>
            <select className="input-dark mt-1" value={form.transmission} onChange={(e) => set("transmission", e.target.value)} data-testid="edit-transmission">
              {TRANSMISSIONS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع الوقود" : "Fuel"}</label>
            <select className="input-dark mt-1" value={form.fuel} onChange={(e) => set("fuel", e.target.value)} data-testid="edit-fuel">
              {FUELS.map(([id, en, ar]) => <option key={id} value={id}>{lang === "ar" ? ar : en}</option>)}
            </select>
          </div>
          {/* iter247 — optional sale-method chips (mirrors SellPage). */}
          <div className="sm:col-span-2">
            <SaleOptionsChips
              value={form.sale_options}
              onChange={(next) => set("sale_options", next)}
              lang={lang}
            />
          </div>
        </div>

        {!["motorcycle", "heavy", "marine", "boat", "salvage"].includes(form.type) && (
          <SpecChecklist value={form.specs} onChange={(s) => set("specs", s)} lang={lang} />
        )}

        <div>
          <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الوصف" : "Description"}</label>
          <textarea
            className="input-dark mt-1"
            rows={4}
            value={form.description}
            onChange={(e) => set("description", e.target.value.slice(0, DESCRIPTION_MAX_LEN))}
            maxLength={DESCRIPTION_MAX_LEN}
            data-testid="edit-description"
          />
          {/* iter220 — live character counter + policy warning */}
          <div className="flex items-center justify-between text-[11px] mt-1">
            {(() => {
              const check = validateDescription(form.description);
              return (
                <span
                  className={check.ok ? "text-[#8a8a8a]" : "text-red-500"}
                  data-testid="edit-description-hint"
                >
                  {check.ok ? "" : (lang === "ar" ? check.message_ar : check.message_en)}
                </span>
              );
            })()}
            <span
              className={((form.description || "").length >= DESCRIPTION_MAX_LEN ? "text-red-500 font-semibold" : "text-[#8a8a8a]")}
              data-testid="edit-description-counter"
            >
              {(form.description || "").length} / {DESCRIPTION_MAX_LEN}
            </span>
          </div>
        </div>

        {/* Images */}
        <div data-testid="edit-images-section">
          <label className="text-xs text-[#6b6b6b] block mb-2">
            {lang === "ar" ? `صور المركبة (حتى ${MAX_IMAGES})` : `Photos (up to ${MAX_IMAGES})`}
            <span className="text-red-600"> *</span>
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
            {form.images.map((url, i) => (
              <div key={`${url}-${i}`} data-img-idx={i}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); moveImage(dragIdx, i); setDragIdx(null); }}
                onDragEnd={() => setDragIdx(null)}
                className={`relative aspect-square rounded-lg overflow-hidden border border-[#0a0a0a]/15 group cursor-move ${dragIdx === i ? "opacity-50" : ""}`} data-testid={`edit-image-${i}`}>
                <img src={url} alt={`image ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                <span
                  onTouchStart={() => setDragIdx(i)}
                  onTouchEnd={(e) => { moveImage(dragIdx, touchOverIdx(e)); setDragIdx(null); }}
                  style={{ touchAction: "none" }}
                  className="absolute bottom-1 start-1 w-7 h-7 rounded-full bg-[#0a0a0a]/70 text-white flex items-center justify-center"
                  data-testid={`edit-image-drag-${i}`}
                  aria-label={lang === "ar" ? "اسحب لتغيير الترتيب" : "Drag to reorder"}
                >
                  <GripVertical className="w-4 h-4" />
                </span>
                {i === 0 && (
                  <span className="absolute top-1 start-1 text-[9px] font-semibold uppercase tracking-wider bg-[#c9a55c] text-[#0a0a0a] px-1.5 py-0.5 rounded">
                    {lang === "ar" ? "غلاف" : "Cover"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute top-1 end-1 w-6 h-6 rounded-full bg-[#0a0a0a]/70 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                  data-testid={`edit-image-remove-${i}`}
                  aria-label="Remove image"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {form.images.length < MAX_IMAGES && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-[#0a0a0a]/20 hover:border-[#c9a55c] flex flex-col items-center justify-center cursor-pointer text-center text-[10px] text-[#6b6b6b] px-2 transition-colors" data-testid="edit-upload-tile">
                {uploadBusy ? <Loader2 className="w-5 h-5 animate-spin text-[#8a6f2e]" /> : (
                  <>
                    <UploadIcon className="w-5 h-5 text-[#8a6f2e] mb-1" />
                    <span>{lang === "ar" ? "أضف صور" : "Add photos"}</span>
                  </>
                )}
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} data-testid="edit-upload" />
              </label>
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="rounded-2xl border border-[#0a0a0a]/10 p-5 bg-[#fafaf7]">
          <div className="overline overline-gold mb-3">{lang === "ar" ? "كيف يتواصل معك المشتري؟" : "How buyers reach you"}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6b6b6b] inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {lang === "ar" ? "الجوال" : "Phone"} <span className="text-red-600">*</span></label>
              <input type="tel" className="input-dark mt-1" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="edit-phone" />
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {lang === "ar" ? "واتساب" : "WhatsApp"}</label>
              <input type="tel" className="input-dark mt-1" value={form.contact_whatsapp} onChange={(e) => set("contact_whatsapp", e.target.value)} data-testid="edit-whatsapp" />
            </div>
          </div>
        </div>

        {/* Periodic inspection */}
        <div className="rounded-2xl border border-[#0a0a0a]/10 p-5 bg-[#fafaf7]">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.periodic_inspection_valid}
              onChange={(e) => set("periodic_inspection_valid", e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#c9a55c]"
              data-testid="edit-periodic-valid"
            />
            <span className="text-sm text-[#3a3a3a]">
              <span className="font-semibold inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "المركبة مفحوصة فحصاً فنياً دورياً سارياً" : "Periodic inspection valid"}</span>
              <p className="text-xs text-[#6b6b6b] mt-1">{lang === "ar" ? "إلزامي لنقل الملكية." : "Required for ownership transfer."}</p>
            </span>
          </label>
          {form.periodic_inspection_valid && (
            <input
              type="date"
              className="input-dark mt-3 max-w-xs"
              value={form.periodic_inspection_expiry}
              onChange={(e) => set("periodic_inspection_expiry", e.target.value)}
              data-testid="edit-periodic-expiry"
            />
          )}
        </div>

        {/* Escrow */}
        {FEATURES.ESCROW_ENABLED && (
        <div className="rounded-2xl border border-[#c9a55c]/40 p-5 bg-gradient-to-br from-[#fdf9ef] to-white">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.escrow_enabled}
              onChange={(e) => set("escrow_enabled", e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#c9a55c]"
              data-testid="edit-escrow-toggle"
            />
            <span className="text-sm text-[#3a3a3a]">
              {lang === "ar" ? "تفعيل ضمان كارتال على هذا الإعلان." : "Enable CARTAL Escrow on this listing."}
            </span>
          </label>
        </div>
        )}

        {err && <div className="text-sm text-red-600" data-testid="edit-error">{err}</div>}

        <div className="flex flex-wrap gap-3 pt-2">
          <button className="btn-primary flex-1 min-w-[200px] justify-center" disabled={busy} data-testid="edit-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {lang === "ar" ? "حفظ التعديلات" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold border-2 border-red-300 text-red-700 hover:bg-red-50 transition-colors"
            data-testid="edit-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            {lang === "ar" ? "حذف الإعلان" : "Delete listing"}
          </button>
        </div>
      </form>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-3xl max-w-md w-full mx-4 p-7 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-red-100">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </span>
              <h3 className="font-display text-xl font-semibold">
                {lang === "ar" ? "تأكيد الحذف" : "Confirm delete"}
              </h3>
            </div>
            <p className="text-sm text-[#525252] leading-relaxed mb-5">
              {lang === "ar"
                ? "سيتم حذف هذا الإعلان نهائياً ولن يكون بالإمكان استرجاعه. هل أنت متأكد؟"
                : "This listing will be permanently deleted and cannot be recovered. Are you sure?"}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="btn-ghost flex-1 justify-center"
                disabled={delBusy}
                data-testid="delete-cancel"
              >
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={delBusy}
                className="inline-flex items-center justify-center gap-2 flex-1 rounded-full px-5 py-2.5 text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors"
                data-testid="delete-confirm"
              >
                {delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {lang === "ar" ? "نعم، احذف نهائياً" : "Yes, delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
